import { useState } from 'react'
import { Clock } from 'lucide-react'
import { LANDING_PAGE_DAY_MOMENTS, LANDING_PAGE_SECTION, LANDING_PAGE_UI } from '@propbix/constants/public'
import { Reveal } from '@/components/Reveal'

type Moment = (typeof LANDING_PAGE_DAY_MOMENTS)[number]

function MomentPreview({ moment }: { moment: Moment }) {
  return (
    <div className="command-desk overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3.5 sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-teal-500/20 text-teal-300">
            <Clock size={15} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold tracking-tight text-white">{moment.previewTitle}</p>
            <p className="truncate text-xs text-slate-400">
              {moment.time} · {moment.title}
            </p>
          </div>
        </div>
        <span className="desk-badge border border-white/10 bg-white/5 text-slate-300">
          {LANDING_PAGE_UI.dayPreviewBadge}
        </span>
      </div>

      <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5">
        {moment.previewLines.map((line, index) => (
          <div
            key={line}
            className={`command-widget p-3.5 ${index === 0 ? 'sm:col-span-2' : ''}`}
          >
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-slate-500">
              {index === 0
                ? LANDING_PAGE_UI.dayPreviewPrimaryLabel
                : `${LANDING_PAGE_UI.dayPreviewUpdateLabel} ${index}`}
            </p>
            <p className="mt-2 text-sm font-medium leading-snug text-slate-100">{line}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

export function DayTimeline() {
  const [activeId, setActiveId] = useState(LANDING_PAGE_DAY_MOMENTS[0].id)
  const active = LANDING_PAGE_DAY_MOMENTS.find((m) => m.id === activeId) ?? LANDING_PAGE_DAY_MOMENTS[0]

  return (
    <section
      id={LANDING_PAGE_SECTION.day}
      className="section-band-ink relative overflow-hidden"
      aria-labelledby="day-heading"
    >
      {/* Subtle grid texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--desk-line) / 0.04) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--desk-line) / 0.04) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'linear-gradient(180deg, transparent 0%, black 20%, black 80%, transparent 100%)',
        }}
        aria-hidden
      />

      <div className="page-shell section-y relative z-[1]">
        {/* Header */}
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="section-label" style={{ color: 'rgb(var(--accent-glow))' }}>
            {LANDING_PAGE_UI.dayLabel}
          </p>
          <h2
            id="day-heading"
            className="section-title mt-3"
            style={{ color: 'rgb(var(--desk-ink))' }}
          >
            {LANDING_PAGE_UI.dayTitle}
          </h2>
          <p className="section-body mx-auto mt-5" style={{ color: 'rgb(var(--desk-muted))' }}>
            {LANDING_PAGE_UI.daySubtitle}
          </p>
        </Reveal>

        {/* Desktop: two-column — timeline left, preview right */}
        <div className="mt-14 hidden md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] md:items-start md:gap-10 lg:gap-14">
          {/* Timeline column */}
          <div role="tablist" aria-label="Day moments">
            <ol className="relative">
              {LANDING_PAGE_DAY_MOMENTS.map((moment, index) => {
                const isActive = moment.id === activeId
                const isLast = index === LANDING_PAGE_DAY_MOMENTS.length - 1
                return (
                  <li key={moment.id} className="relative flex gap-5">
                    {/* Connector line */}
                    {!isLast ? (
                      <span
                        className="absolute left-[15px] top-[34px] bottom-0 w-px"
                        style={{
                          background: isActive
                            ? 'linear-gradient(to bottom, rgb(var(--accent-glow) / 0.6), rgb(var(--desk-line) / 0.2))'
                            : 'linear-gradient(to bottom, rgb(var(--desk-line) / 0.25), rgb(var(--desk-line) / 0.08))',
                        }}
                        aria-hidden
                      />
                    ) : null}

                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveId(moment.id)}
                      className="group relative z-[1] flex w-full items-start gap-5 pb-7 text-left last:pb-0 focus-visible:outline-none"
                    >
                      {/* Timeline dot */}
                      <span
                        className="mt-1 flex size-[30px] shrink-0 items-center justify-center rounded-full transition-all duration-300"
                        style={{
                          background: isActive
                            ? 'rgb(var(--accent-glow) / 0.2)'
                            : 'rgb(var(--desk-panel) / 0.8)',
                          border: isActive
                            ? '1.5px solid rgb(var(--accent-glow) / 0.7)'
                            : '1.5px solid rgb(var(--desk-line) / 0.2)',
                          boxShadow: isActive ? '0 0 14px rgb(var(--accent-glow) / 0.25)' : 'none',
                        }}
                        aria-hidden
                      >
                        <span
                          className="size-2 rounded-full transition-all duration-300"
                          style={{
                            background: isActive
                              ? 'rgb(var(--accent-glow))'
                              : 'rgb(var(--desk-line) / 0.4)',
                          }}
                        />
                      </span>

                      {/* Content */}
                      <div className="min-w-0 pt-0.5">
                        <p
                          className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] transition-colors duration-200"
                          style={{ color: isActive ? 'rgb(var(--accent-glow))' : 'rgb(var(--desk-muted) / 0.6)' }}
                        >
                          {moment.time}
                        </p>
                        <p
                          className="mt-0.5 text-sm font-semibold leading-snug transition-colors duration-200"
                          style={{ color: isActive ? 'rgb(var(--desk-ink))' : 'rgb(var(--desk-muted))' }}
                        >
                          {moment.title}
                        </p>
                        <p
                          className="mt-1.5 text-sm leading-6 transition-colors duration-200"
                          style={{ color: isActive ? 'rgb(var(--desk-muted))' : 'rgb(var(--desk-muted) / 0.6)' }}
                        >
                          {moment.detail}
                        </p>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ol>
          </div>

          {/* Preview column — sticky */}
          <div className="sticky top-24" aria-live="polite">
            <Reveal key={active.id} delay={0.05}>
              <MomentPreview moment={active} />
            </Reveal>
          </div>
        </div>

        {/* Mobile: stacked list then preview */}
        <div className="mt-10 md:hidden">
          <div
            className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Day moments"
          >
            {LANDING_PAGE_DAY_MOMENTS.map((moment) => {
              const isActive = moment.id === activeId
              return (
                <button
                  key={moment.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveId(moment.id)}
                  className="shrink-0 rounded-[var(--radius-control)] px-3.5 py-2 text-xs font-semibold transition-all duration-200"
                  style={{
                    background: isActive ? 'rgb(var(--accent-glow) / 0.15)' : 'rgb(var(--desk-panel) / 0.6)',
                    border: isActive
                      ? '1px solid rgb(var(--accent-glow) / 0.4)'
                      : '1px solid rgb(var(--desk-line) / 0.15)',
                    color: isActive ? 'rgb(var(--accent-glow))' : 'rgb(var(--desk-muted))',
                  }}
                >
                  {moment.time}
                </button>
              )
            })}
          </div>

          <div className="mt-5">
            <p
              className="text-sm font-semibold"
              style={{ color: 'rgb(var(--desk-ink))' }}
            >
              {active.title}
            </p>
            <p className="mt-1.5 text-sm leading-6" style={{ color: 'rgb(var(--desk-muted))' }}>
              {active.detail}
            </p>
          </div>

          <div className="mt-6" aria-live="polite">
            <MomentPreview moment={active} />
          </div>
        </div>
      </div>
    </section>
  )
}
